import { useState, useRef, useEffect, type KeyboardEvent, type FormEvent } from 'react';

const SYSTEM_PROMPT = `You are a senior software architect specializing in distributed systems, embedded software, drone fleet management, and cloud-native backends. Your job is to extract a complete architectural design from the user through structured questioning.

PROJECT CONTEXT: Carrier Pigeon — autonomous drone fleet system using Pixhawk 4 Mini, Raspberry Pi Zero 2W, LTE connectivity, Go microservices, Kubernetes, NATS, PostgreSQL/PostGIS, InfluxDB, Redis, Grafana.

CONVERSATION PHASES:

PHASE 1 — GRILLING (2-3 rounds of targeted questions):
Ask 3-5 focused, technical questions per round. Be specific and direct. Uncover:
- Scope boundaries (what's IN, what's OUT)
- Integration points with existing systems
- Non-functional requirements (latency, scale, fault tolerance)
- Data flows and ownership
- Failure modes and edge cases
- Security and auth concerns
- Deployment constraints

PHASE 2 — SYNTHESIS:
After grilling, output a complete GitHub Issue in this EXACT format:

---ISSUE_START---
## Title
[Concise, imperative title like "feat: implement X for Y"]

## Labels
[comma-separated: feature/bug/maintenance, component name]

## Description
[2-3 sentences: what, why, scope boundary]

## Architecture

### Component Diagram
\`\`\`mermaid
[component or flowchart diagram — GitHub-compatible syntax only]
\`\`\`

### Data Flow
\`\`\`mermaid
[sequence or flowchart diagram]
\`\`\`

## Non-Functional Requirements
- Latency: ...
- Scale: ...
- Fault tolerance: ...
- Security: ...

## User Stories

### US-1: [Name]
**As a** [role], **I want** [goal] **so that** [benefit].

**Acceptance Criteria:**
- [ ] ...

### US-2: [Name]
...

## Out of Scope
- ...

## Open Questions
- ...
---ISSUE_END---

RULES:
- During grilling: ask questions only, no architecture yet
- Questions must be specific and technical, not generic
- Maximum 3 grilling rounds before synthesizing
- Mermaid diagrams must use only GitHub-supported syntax (no subgraph labels with spaces unquoted, no unsupported shapes)
- All output in English
- Be direct, be a senior engineer not a consultant`;

type Phase = 'idle' | 'grilling' | 'done';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

function parseIssue(text: string): string | null {
  const start = text.indexOf('---ISSUE_START---');
  const end = text.indexOf('---ISSUE_END---');
  if (start === -1 || end === -1) return null;
  return text.slice(start + 17, end).trim();
}

interface MessageBubbleProps {
  msg: ChatMessage;
}

function MessageBubble({ msg }: MessageBubbleProps) {
  const isUser = msg.role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '16px',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      {!isUser && (
        <div
          aria-hidden="true"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00d4aa, #0066ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            flexShrink: 0,
            marginTop: '2px',
          }}
        >
          🏛
        </div>
      )}
      <div
        style={{
          maxWidth: '75%',
          padding: '12px 16px',
          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          background: isUser
            ? 'linear-gradient(135deg, #0066ff, #0044cc)'
            : 'rgba(255,255,255,0.06)',
          border: isUser ? 'none' : '1px solid rgba(255,255,255,0.1)',
          color: '#e8eaf0',
          fontSize: '14px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {msg.content}
      </div>
    </div>
  );
}

interface IssueOutputProps {
  content: string;
}

function IssueOutput({ content }: IssueOutputProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        background: 'rgba(0, 212, 170, 0.05)',
        border: '1px solid rgba(0, 212, 170, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginTop: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <span
          style={{
            color: '#00d4aa',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          ✦ GitHub Issue Ready
        </span>
        <button
          onClick={handleCopy}
          aria-label={copied ? 'Copied to clipboard' : 'Copy markdown to clipboard'}
          style={{
            background: copied ? 'rgba(0,212,170,0.2)' : 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
            color: copied ? '#00d4aa' : '#a0a8c0',
            cursor: 'pointer',
            fontSize: '12px',
            padding: '5px 12px',
            transition: 'all 0.2s',
          }}
        >
          {copied ? '✓ Copied!' : 'Copy Markdown'}
        </button>
      </div>
      <pre
        style={{
          color: '#c8d0e8',
          fontSize: '12px',
          lineHeight: '1.7',
          fontFamily: "'JetBrains Mono', monospace",
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: '400px',
          overflowY: 'auto',
          paddingRight: '8px',
        }}
      >
        {content}
      </pre>
    </div>
  );
}

const EXAMPLE_PROMPTS = [
  'Mission planning service for drone routes',
  'RTH failsafe logic on onboard Go binary',
  'WebSocket telemetry gateway',
] as const;

const PHASE_LABEL: Record<Phase, string> = {
  idle: 'Describe your feature or system',
  grilling: 'Grilling — answering narrows the design',
  done: 'Architecture complete',
};

const PHASE_COLOR: Record<Phase, string> = {
  idle: '#a0a8c0',
  grilling: '#f0a500',
  done: '#00d4aa',
};

export function ArchitectureAgent() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [issueContent, setIssueContent] = useState<string | null>(null);
  const [roundCount, setRoundCount] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(newMessages);
    setInput('');
    setApiError(null);
    setLoading(true);

    if (phase === 'idle') setPhase('grilling');

    try {
      const response = await fetch('/anthropic/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: newMessages,
        }),
      });

      if (!response.ok) {
        const errBody = await response.text();
        throw new Error(`API error ${response.status}: ${errBody}`);
      }

      const data = (await response.json()) as { content?: Array<{ text: string }> };
      const text = data.content?.[0]?.text ?? 'Error: no response';

      const issue = parseIssue(text);
      const displayText = issue
        ? text
            .replace('---ISSUE_START---', '')
            .replace('---ISSUE_END---', '')
            .replace(issue, '')
            .trim() || '✓ Architecture design complete. GitHub Issue generated below.'
        : text;

      setMessages((prev) => [...prev, { role: 'assistant', content: displayText }]);

      if (issue) {
        setIssueContent(issue);
        setPhase('done');
      } else {
        setRoundCount((r) => r + 1);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setApiError(message);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error connecting to API: ${message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const handleInput = (e: FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
  };

  const reset = () => {
    setMessages([]);
    setInput('');
    setPhase('idle');
    setIssueContent(null);
    setRoundCount(0);
    setApiError(null);
  };

  const phaseLabel =
    phase === 'grilling'
      ? `Grilling round ${roundCount + 1} — answering narrows the design`
      : PHASE_LABEL[phase];
  const phaseColor = PHASE_COLOR[phase];

  return (
    <div
      style={{
        minHeight: '100%',
        background: '#0d0f1a',
        fontFamily: "'Inter', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 16px',
        overflowY: 'auto',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;600&display=swap');
        @keyframes arch-pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {/* Header */}
      <div style={{ width: '100%', maxWidth: '760px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '11px',
                color: '#0066ff',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '6px',
              }}
            >
              Carrier Pigeon // Tooling
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: '22px',
                fontWeight: '600',
                color: '#e8eaf0',
                letterSpacing: '-0.02em',
              }}
            >
              Architecture Agent
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#606880' }}>
              Describe → Grilled → GitHub Issue
            </p>
          </div>
          {messages.length > 0 && (
            <button
              onClick={reset}
              aria-label="Reset conversation"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#606880',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '6px 14px',
                transition: 'all 0.2s',
              }}
            >
              ↺ Reset
            </button>
          )}
        </div>

        {/* Phase indicator */}
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            role="status"
            aria-label={`Phase: ${phaseLabel}`}
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: phaseColor,
              boxShadow: `0 0 8px ${phaseColor}`,
            }}
          />
          <span
            style={{
              fontSize: '12px',
              color: phaseColor,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {phaseLabel}
          </span>
        </div>

        {phase === 'grilling' && (
          <div
            role="progressbar"
            aria-valuenow={Math.min((roundCount / 3) * 100, 90)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Grilling progress"
            style={{
              marginTop: '8px',
              height: '2px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '1px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.min((roundCount / 3) * 100, 90)}%`,
                background: 'linear-gradient(90deg, #f0a500, #ff6b35)',
                borderRadius: '1px',
                transition: 'width 0.5s ease',
              }}
            />
          </div>
        )}

        {apiError && (
          <div
            role="alert"
            style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(255,80,80,0.1)',
              border: '1px solid rgba(255,80,80,0.3)',
              borderRadius: '8px',
              color: '#ff8080',
              fontSize: '12px',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {apiError.includes('401') || apiError.includes('403')
              ? 'API key missing or invalid. Set ANTHROPIC_API_KEY in your .env and restart the dev server.'
              : apiError}
          </div>
        )}
      </div>

      {/* Chat area */}
      <div style={{ width: '100%', maxWidth: '760px', flex: 1, minHeight: '300px' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#404560' }}>
            <div aria-hidden="true" style={{ fontSize: '40px', marginBottom: '16px' }}>
              🏛
            </div>
            <p style={{ fontSize: '15px', margin: '0 0 8px', color: '#606880' }}>
              Describe the component, service, or feature you want to build.
            </p>
            <p style={{ fontSize: '13px', margin: 0, color: '#404560' }}>
              The agent will ask targeted questions, then produce a GitHub Issue with architecture
              diagrams.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginTop: '24px',
              }}
            >
              {EXAMPLE_PROMPTS.map((ex) => (
                <button
                  key={ex}
                  onClick={() => setInput(ex)}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '20px',
                    color: '#606880',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '6px 14px',
                    transition: 'all 0.2s',
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ paddingBottom: '8px' }}>
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}
            {loading && (
              <div
                aria-live="polite"
                aria-label="Architect is thinking"
                style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00d4aa, #0066ff)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    flexShrink: 0,
                  }}
                >
                  🏛
                </div>
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '18px 18px 18px 4px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                  }}
                >
                  {([0, 1, 2] as const).map((i) => (
                    <div
                      key={i}
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4060a0',
                        animation: `arch-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            {issueContent !== null && <IssueOutput content={issueContent} />}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div
        style={{
          width: '100%',
          maxWidth: '760px',
          marginTop: '16px',
          position: 'sticky',
          bottom: '16px',
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '14px',
            padding: '12px 14px',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-end',
            backdropFilter: 'blur(12px)',
          }}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            onInput={handleInput}
            placeholder={
              phase === 'idle'
                ? 'Describe the system or feature you want to architect...'
                : 'Answer the questions above...'
            }
            disabled={loading || phase === 'done'}
            rows={1}
            aria-label="Message input"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: '#e8eaf0',
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: "'Inter', system-ui, sans-serif",
              maxHeight: '120px',
              overflow: 'auto',
              padding: 0,
              resize: 'none',
            }}
          />
          <button
            onClick={() => void sendMessage()}
            disabled={loading || !input.trim() || phase === 'done'}
            aria-label="Send message"
            style={{
              background:
                loading || !input.trim() || phase === 'done'
                  ? 'rgba(255,255,255,0.06)'
                  : 'linear-gradient(135deg, #0066ff, #0044cc)',
              border: 'none',
              borderRadius: '8px',
              color: loading || !input.trim() || phase === 'done' ? '#404560' : '#fff',
              cursor: loading || !input.trim() || phase === 'done' ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              padding: '8px 14px',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
          >
            ↑
          </button>
        </div>
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#303450', margin: '8px 0 0' }}>
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
